import i18n from 'i18next';
import {AppLanguage, formatDateTime, formatNumber, LocaleConfig} from 'platform/locale';

// eslint-disable-next-line no-restricted-imports -- Intended
import {isNil, isNotNil, path} from 'ramda';
import {isArray, isNumber} from 'ramda-adjunct';

import {formatFileSize, parseDate} from 'shared/web';

import {CheckboxCellRenderer} from '../components/cells/CheckboxCellRenderer';
import {DateCellRenderer} from '../components/cells/DateCellRenderer';
import {DateTimeCellRenderer} from '../components/cells/DateTImeCellRenderer';
import {FileCellRenderer} from '../components/cells/FileCellRenderer';
import {FilePlainCellRenderer} from '../components/cells/FilePlainCellRenderer';
import {FileSizeCellRenderer} from '../components/cells/FileSizeCellRenderer';
import {FlagCellRenderer} from '../components/cells/FlagCellRenderer';
import {IconCellRenderer} from '../components/cells/IconCellRenderer';
import {IntegerCellRenderer} from '../components/cells/IntegerCellRenderer';
import {LinkCellRenderer} from '../components/cells/LinkCellRenderer';
import {MetaTextCellRenderer} from '../components/cells/MetaTextCellRenderer';
import {MoneyCellRenderer} from '../components/cells/MoneyCellRenderer';
import {PlainCellRenderer} from '../components/cells/PlainCellRenderer';
import {TagCellRenderer, ValueProps} from '../components/cells/TagCellRenderer';
import {ThumbnailCellRenderer} from '../components/cells/ThumbnailCellRenderer';
import {TimeIntervalCellRenderer} from '../components/cells/TimeIntervalCellRenderer';
import {BuildinMenuItem} from '../hooks/useContextMenu';
import {ColDef} from '../types/AgGridTypes';
import {TransformedDefinitionApiResponse} from '../types/Api';
import {EntityResourceResolver} from '../types/EntityResourceResolver';
import {FlagPropsGetterParams} from '../types/FlagPropsGetter';
import {LinkClickOverride} from '../types/LinkClickOverride';
import {LinkPropsGetterParams} from '../types/LinkPropsGetter';
import {RowData} from '../types/RowData';
import {TooltipGetterProps} from '../types/TooltipGetter';
import {formatTimeToResponse} from './formatTimeToResponse';
import {getFlagProps} from './getFlagProps';
import {isEntityColumnResponseBody} from './isEntityColumnResponseBody';
import {isEnumColumnResponseBody} from './isEnumColumnResponseBody';

const disableCopyActions: ColDef['contextMenuItems'] = (params) => {
  const menuItems = params.context.getContextMenuItems(params) as BuildinMenuItem[];

  return menuItems.filter(
    (item) =>
      ![
        BuildinMenuItem.copy,
        BuildinMenuItem.copyWithHeaders,
        BuildinMenuItem.copyWithGroupHeaders,
      ].includes(item)
  );
};

export const columnToTypeColDefs = (
  column: TransformedDefinitionApiResponse['columns'][number],
  localeConfig: LocaleConfig,
  language: AppLanguage,
  entityResourceResolver?: EntityResourceResolver,
  linkClickOverride?: LinkClickOverride
): Partial<ColDef> => {
  const colDef: Partial<ColDef> = {};

  const dataPath = column.key;
  const plainPath = [column.key, 'value'];
  const labelPath = [column.key, 'value', 'label'];
  const urlPath = [column.key, 'value', 'url'];
  const keyPath = [column.key, 'value', 'key'];
  const filePath = [column.key, 'value'];

  const plainValueGetter = ({data}: {data?: RowData}) => path(plainPath, data);
  const labelValueGetter = ({data}: {data?: RowData}) => path(labelPath, data);
  const urlValueGetter = ({data}: {data?: RowData}) => path(urlPath, data);
  const keyValueGetter = ({data}: {data?: RowData}) => path(keyPath, data);

  colDef.valueGetter = plainValueGetter;

  switch (column.type) {
    case 'CODE_LIST':
      colDef.cellClass = ['PlainCellRenderer'];
      colDef.cellRenderer = PlainCellRenderer;
      colDef.cellRendererParams = {
        getTooltipProps: ({data}: TooltipGetterProps) => {
          const tooltipData = path([column.key, 'value'], data);

          if (!isArray(tooltipData)) {
            return {
              label: path(['label'], tooltipData),
              overflowWrap: 'anywhere',
            };
          }

          return tooltipData.map((innerData) => ({
            label: path(['label'], innerData),
            overflowWrap: 'anywhere',
          }));
        },
      };
      colDef.valueGetter = ({data}: {data?: RowData}) => {
        const valueData = path([column.key, 'value'], data);

        if (isNil(valueData)) {
          return;
        }

        if (!isArray(valueData)) {
          return path(['label'], valueData);
        }

        return valueData.map((innerData) => path(['label'], innerData));
      };
      colDef.chartDataType = 'category';
      break;
    case 'TEXT':
      colDef.valueGetter = ({data}: {data?: RowData}) => path([column.key, 'value'], data);
      colDef.chartDataType = 'category';
      colDef.cellClass = ['PlainCellRenderer'];

      if (column.renderer.type === 'METATEXT') {
        colDef.cellRenderer = MetaTextCellRenderer;
        colDef.cellRendererParams = {
          getTooltipProps: ({data}: TooltipGetterProps) => {
            const rawValue = path([column.key, 'value'], data);
            if (typeof rawValue !== 'string') {
              return {label: rawValue, overflowWrap: 'anywhere'};
            }
            // Parse meta text to plain text for tooltip
            const plainText = rawValue.replace(/@\[([^\]]+)\]\(([^)]+)\)/g, '$1');
            return {label: plainText, overflowWrap: 'anywhere'};
          },
        };
        break;
      }

      if (column.renderer.type === 'ICON') {
        colDef.cellRenderer = IconCellRenderer;
        colDef.cellRendererParams = {
          getTooltipProps: ({data}: TooltipGetterProps) => ({
            label: path([column.key, 'value'], data),
            overflowWrap: 'anywhere',
          }),
        };
        break;
      }

      colDef.cellRenderer = PlainCellRenderer;
      colDef.cellRendererParams = {
        getTooltipProps: ({data}: TooltipGetterProps) => ({
          label: path([column.key, 'value'], data),
          overflowWrap: 'anywhere',
        }),
      };
      break;
    case 'TIME_INTERVAL':
      colDef.cellClass = ['TimeIntervalCellRenderer'];
      colDef.cellRenderer = TimeIntervalCellRenderer;
      colDef.valueFormatter = ({value}) => {
        if (!isNumber(value)) {
          return value;
        }

        return formatTimeToResponse(value);
      };
      colDef.cellRendererParams = {
        getTooltipProps: ({data}: TooltipGetterProps) => {
          const value = path([column.key, 'value'], data);

          return {
            label: isNumber(value) ? formatTimeToResponse(value) : value,
            overflowWrap: 'anywhere',
          };
        },
      };
      colDef.valueGetter = ({data}: {data?: RowData}) => path([column.key, 'value'], data);
      colDef.chartDataType = 'category';
      break;
    case 'ENTITY':
      if (column.renderer.type === 'LINK' && isEntityColumnResponseBody(column)) {
        colDef.cellRenderer = LinkCellRenderer;
        colDef.cellRendererParams = {
          linkClickOverride,
          getTooltipProps: ({data}: TooltipGetterProps) => {
            const linkData = path(labelPath, data);

            if (!isArray(linkData)) {
              return {label: path(['value', 'label'], data), overflowWrap: 'anywhere'};
            }

            return linkData.map((link) => ({
              label: path(['label'], link),
              overflowWrap: 'anywhere',
            }));
          },
          getLinkProps: ({data}: LinkPropsGetterParams) => {
            const linkData = path([column.key, 'value'], data);

            if (isNil(linkData)) {
              return;
            }

            if (!isArray(linkData)) {
              return {
                to:
                  entityResourceResolver &&
                  entityResourceResolver(
                    path(['resourceId'], linkData ?? {}) ?? column?.resourceId,
                    path([column.key, 'value', 'id'], data ?? {}) as string,
                    path([column.key, 'value', 'additionalParameters'], data ?? {})
                  ),
              };
            }

            return linkData.map((link) => ({
              to:
                entityResourceResolver &&
                entityResourceResolver(
                  link?.resourceId ?? column?.resourceId,
                  path(['id'], link),
                  link?.additionalParameters
                ),
            }));
          },
        };
        colDef.valueGetter = ({data}: {data?: RowData}) => {
          const linkData = path([column.key, 'value'], data);

          if (isNil(linkData)) {
            return;
          }

          if (!isArray(linkData)) {
            return path(['label'], linkData);
          }

          return linkData.map((link) => path(['label'], link));
        };
      } else {
        colDef.cellClass = ['PlainCellRenderer'];
        colDef.cellRenderer = PlainCellRenderer;
        colDef.cellRendererParams = {
          getTooltipProps: ({data}: TooltipGetterProps) => {
            const linkData = path([column.key, 'value'], data);

            if (!isArray(linkData)) {
              return {label: path(['label'], linkData), overflowWrap: 'anywhere'};
            }

            return {
              label: linkData.map((link) => path(['label'], link)).join(', '),
              overflowWrap: 'anywhere',
            };
          },
        };
        colDef.valueGetter = ({data}: {data?: RowData}) => {
          const linkData = path([column.key, 'value'], data);

          if (isNil(linkData)) {
            return;
          }

          if (!isArray(linkData)) {
            return path(labelPath, data);
          }

          return linkData.map((link) => path(['label'], link));
        };
      }

      colDef.chartDataType = 'category';
      break;
    case 'MONEY':
      if (column.renderer.type === 'MONEY' || column.renderer.type === 'MONEY_WITHOUT_DECIMAL') {
        const decimalPlaces = column.renderer.decimalPlaces ?? 2;

        colDef.cellRenderer = MoneyCellRenderer;
        colDef.cellClass = ['MoneyCellRenderer', 'ag-right-aligned-cell'];

        colDef.cellRendererParams = {
          getTooltipProps: ({data}: TooltipGetterProps) => {
            const money = path([column.key, 'value'], data) as
              | {amount: number; currency: string}
              | undefined;
            return money ? `${money.amount} ${money.currency}` : '';
          },
          decimalPlaces,
        };
        colDef.valueGetter = plainValueGetter;
        colDef.valueFormatter = ({value}) =>
          (isNotNil(value) && formatNumber(localeConfig, value.amount, decimalPlaces)) || '';
        colDef.chartDataType = 'series';
      }
      break;
    case 'ENUM':
      if (column.renderer.type === 'FLAG' && isEnumColumnResponseBody(column)) {
        colDef.cellRenderer = FlagCellRenderer;
        colDef.cellClass = ['FlagCellRenderer'];
        colDef.cellRendererParams = {
          getTooltipProps: ({data}: TooltipGetterProps) => {
            const linkData = path(labelPath, data);

            if (!isArray(linkData)) {
              return {label: linkData, overflowWrap: 'anywhere'};
            }

            return {label: linkData.map((link) => path(['label'], link)), overflowWrap: 'anywhere'};
          },
          getFlagProps: ({data}: FlagPropsGetterParams) =>
            getFlagProps(column.enumType, keyValueGetter({data}) as string),
        };
        colDef.valueGetter = ({data}: {data?: RowData}) => {
          const flagData = path([dataPath], data);

          if (isNil(flagData)) {
            return;
          }

          return flagData;
        };
        colDef.valueFormatter = ({value}) => {
          if (isNil(value?.value)) {
            return '';
          }
          const valueToRender = value.value as ValueProps | ValueProps[];

          return isArray(valueToRender)
            ? valueToRender
                .filter((v) => v)
                .map((v) => v.label)
                .join(', ')
            : valueToRender.label;
        };
      } else if (column.renderer.type === 'ICON') {
        colDef.cellClass = ['IconCellRenderer'];
        colDef.cellRenderer = IconCellRenderer;
        colDef.cellRendererParams = {
          getTooltipProps: ({data}: TooltipGetterProps) => ({
            label: labelValueGetter({data}),
            overflowWrap: 'anywhere',
          }),
        };
        colDef.valueGetter = labelValueGetter;
      } else {
        colDef.cellClass = ['PlainCellRenderer'];
        colDef.cellRenderer = PlainCellRenderer;
        colDef.cellRendererParams = {
          getTooltipProps: ({data}: TooltipGetterProps) => ({
            label: labelValueGetter({data}),
            overflowWrap: 'anywhere',
          }),
        };
        colDef.valueGetter = labelValueGetter;
      }

      colDef.chartDataType = 'category';
      break;
    case 'DATE_TIME':
      if (column.renderer.type === 'DATE_TIME') {
        colDef.cellClass = ['DateTimeCellRenderer'];
        colDef.cellRenderer = DateTimeCellRenderer;
      } else {
        colDef.cellClass = ['DateCellRenderer'];
        colDef.cellRenderer = DateCellRenderer;
      }

      colDef.valueGetter = ({data}: {data?: RowData}) => path([column.key, 'value'], data);
      colDef.cellRendererParams = {
        getTooltipProps: ({data}: TooltipGetterProps) => ({
          label: path([column.key, 'value'], data),
        }),
      };
      colDef.valueFormatter = ({value}) => {
        const parsedDate = parseDate(value);
        if (isNil(parsedDate)) {
          return '';
        }

        return formatDateTime(
          localeConfig,
          language,
          column.renderer.type === 'DATE_TIME' ? 'dateTimeShort' : 'dateShort',
          parsedDate as Date
        );
      };
      break;
    case 'DATE':
      colDef.cellClass = ['DateCellRenderer'];
      colDef.cellRenderer = DateCellRenderer;
      colDef.cellRendererParams = {
        getTooltipProps: ({data}: TooltipGetterProps) => ({
          label: path([column.key, 'value'], data),
        }),
      };
      colDef.valueGetter = ({data}: {data?: RowData}) => path([column.key, 'value'], data);
      colDef.valueFormatter = ({value}) => {
        const parsedDate = parseDate(value);
        if (isNil(parsedDate)) {
          return '';
        }
        return formatDateTime(localeConfig, language, 'dateShort', parsedDate as Date);
      };
      break;
    case 'BOOLEAN':
      if (column.renderer.type === 'CHECKBOX') {
        colDef.cellClass = ['CheckboxCellRenderer'];
        colDef.cellRenderer = CheckboxCellRenderer;
      } else {
        colDef.cellClass = ['PlainCellRenderer'];
        colDef.cellRenderer = PlainCellRenderer;
      }
      colDef.valueFormatter = ({value}) => {
        if (value === true) {
          return i18n.t('general.labels.yes');
        }
        if (value === false) {
          return i18n.t('general.labels.no');
        }
        return '';
      };
      colDef.cellRendererParams = {
        getTooltipProps: ({data}: TooltipGetterProps) => ({
          label:
            path([column.key, 'value'], data) === true
              ? i18n.t('general.labels.yes')
              : i18n.t('general.labels.no'),
        }),
      };
      break;
    case 'IMAGE':
      colDef.cellRenderer = ThumbnailCellRenderer;
      colDef.cellClass = ['ThumbnailCellRenderer'];
      colDef.valueGetter = urlValueGetter;
      colDef.chartDataType = 'excluded';
      colDef.contextMenuItems = disableCopyActions;
      break;
    case 'INTEGER':
      if (column.renderer.type === 'PLAIN') {
        colDef.cellRenderer = PlainCellRenderer;
        colDef.cellClass = ['PlainCellRenderer', 'ag-right-aligned-cell'];
      } else if (column.renderer.type === 'FILE_SIZE') {
        colDef.cellRenderer = FileSizeCellRenderer;
        colDef.cellClass = ['FileSizeCellRenderer', 'ag-right-aligned-cell'];
        colDef.valueFormatter = ({value}) => (isNumber(value) ? formatFileSize(value) : '');
      } else {
        colDef.cellRenderer = IntegerCellRenderer;
        colDef.cellClass = ['IntegerCellRenderer', 'ag-right-aligned-cell'];
      }

      colDef.cellRendererParams = {
        getTooltipProps: ({data}: TooltipGetterProps) => ({
          label: path([column.key, 'value'], data),
          overflowWrap: 'anywhere',
        }),
        decimalPlaces: 0,
      };

      colDef.chartDataType = 'series';
      break;
    case 'FLOAT':
      colDef.cellClass = ['IntegerCellRenderer', 'ag-right-aligned-cell'];
      colDef.cellRenderer = IntegerCellRenderer;
      colDef.cellRendererParams = {
        getTooltipProps: ({data}: TooltipGetterProps) => ({
          label: path([column.key, 'value'], data),
          overflowWrap: 'anywhere',
        }),
        decimalPlaces: 0,
      };
      if (column.renderer.type === 'DECIMAL') {
        colDef.cellRendererParams.decimalPlaces = column.renderer.decimalPlaces;
      }

      colDef.chartDataType = 'series';
      break;
    case 'TAG_VALUE':
      colDef.cellClass = ['TagCellRenderer'];
      colDef.cellRenderer = TagCellRenderer;
      colDef.valueGetter = ({data}: {data?: RowData}) => {
        const flagData = path(plainPath, data);

        if (isNil(flagData)) {
          return;
        }

        return flagData;
      };
      colDef.valueFormatter = ({value}) => {
        if (isNil(value?.value)) {
          return '';
        }
        const valueToRender = value as ValueProps | ValueProps[];

        return isArray(valueToRender)
          ? valueToRender
              .filter((v) => v)
              .map((v) => v.label)
              .join(', ')
          : valueToRender.label;
      };
      colDef.cellRendererParams = {
        getTooltipProps: ({data}: TooltipGetterProps) => {
          const linkData = path(plainPath, data);

          if (!isArray(linkData)) {
            return {label: path(['value', 'label'], linkData), overflowWrap: 'anywhere'};
          }

          return {label: linkData.map((link) => path(['label'], link)), overflowWrap: 'anywhere'};
        },
      };
      break;
    case 'FILE':
      if (column.renderer.type === 'PLAIN') {
        colDef.cellRenderer = FilePlainCellRenderer;
      } else {
        colDef.cellRenderer = FileCellRenderer;
      }
      colDef.contextMenuItems = disableCopyActions;
      colDef.valueGetter = ({data}: {data?: RowData}) => {
        const fileData = path(filePath, data);

        if (isNil(fileData)) {
          return;
        }

        return fileData;
      };
  }

  return colDef;
};
